import { light } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useEffect, useState } from 'react'
import classNames from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'
import toast from 'react-hot-toast'
import { formatISO } from 'date-fns'
import { Button, Card, Icon, Text, TimePickerInput } from 'src/components/ui'
import { isDefined, isTimeRange } from 'src/types'
import {
  useStartModelTrainingMutation,
  useUpdateModelTrainingConfiguration,
} from 'models/api'
import {
  FetchModel,
  GqlModelConfigurationName,
  GqlSetModelTrainingConfigurationInput,
} from 'src/services'
import { timeRangeOrPeriodToTimeRange } from 'src/utility/time'
import useTimeRange, { TimeRangeProvider } from 'src/contexts/timeRange'
import { AdvancedTrainingPeriod } from './components'
import { useTrainingPeriodStore } from './training-period.state'

type Props = {
  model: FetchModel
}

export function TrainingPeriod({ model }: Props): JSX.Element {
  return (
    <TimeRangeProvider>
      <TrainingPeriodWithTimeRangeProvider model={model} />
    </TimeRangeProvider>
  )
}

function TrainingPeriodWithTimeRangeProvider({ model }: Props): JSX.Element {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false)
  const { trainingPeriod, excludedPeriods, setTrainingPeriod, resetState } =
    useTrainingPeriodStore()
  const updateTrainingConfigMutation = useUpdateModelTrainingConfiguration()
  const startModelTraining = useStartModelTrainingMutation(model)
  const { lizardModelTraining } = useFlags()
  const { setTimeRange } = useTimeRange()

  const modelHasErrors =
    model.method.name === 'Nowcast' && model.inputTags.includes(model.tag)

  useEffect(() => {
    resetState({
      trainingEnd: model.trainingEnd,
      trainingStart: model.trainingStart,
      trainingPeriod: model.trainingPeriod ?? undefined,
    })
  }, [
    model.id,
    model.trainingEnd,
    model.trainingPeriod,
    model.trainingStart,
    resetState,
  ])

  useEffect(() => {
    setTimeRange(timeRangeOrPeriodToTimeRange(trainingPeriod))
  }, [setTimeRange, trainingPeriod])

  const handleTraining = async (lizard?: boolean): Promise<void> => {
    if (lizard && !lizardModelTraining) return
    const trainingConfig: GqlSetModelTrainingConfigurationInput = {
      modelId: model.id,
      exclusions: excludedPeriods
        .map(({ timeRange }) => timeRange)
        .filter(isDefined)
        .map(timeRange => ({
          start: formatISO(timeRange.from),
          end: formatISO(timeRange.to),
        })),
      trainingPeriod: isTimeRange(trainingPeriod)
        ? {
            timeRange: {
              start: formatISO(trainingPeriod.from),
              end: formatISO(trainingPeriod.to),
            },
          }
        : {
            period: trainingPeriod.period,
          },
    }
    await updateTrainingConfigMutation.mutateAsync(trainingConfig, {
      onError: () => {
        toast.error('Failed to update model training configuration', {
          position: 'top-right',
        })
      },
    })

    await startModelTraining.mutateAsync(
      {
        id: model.id,
        configurationName: lizard
          ? GqlModelConfigurationName.Lizard
          : undefined,
      },
      {
        onError: () => {
          toast.error('Failed to start model training', {
            position: 'top-right',
          })
        },
      },
    )
  }

  return (
    <Card>
      <div className="flex items-end justify-between">
        <div>
          <Text variant="title" bold>
            Model training settings
          </Text>
        </div>
        <div className="flex items-center gap-s">
          {lizardModelTraining && model.__typename === 'ForecastModel' && (
            <Button
              title="Train Model v2"
              variant="primary"
              className="w-[250px]"
              onClick={() => handleTraining(true)}
              isPending={
                updateTrainingConfigMutation.isLoading ||
                startModelTraining.isLoading
              }
              disabled={
                modelHasErrors ||
                updateTrainingConfigMutation.isLoading ||
                startModelTraining.isLoading
              }
            />
          )}

          <Button
            variant="primary"
            title="Train Model"
            className="w-[250px]"
            onClick={() => handleTraining()}
            isPending={
              updateTrainingConfigMutation.isLoading ||
              startModelTraining.isLoading
            }
            disabled={
              modelHasErrors ||
              updateTrainingConfigMutation.isLoading ||
              startModelTraining.isLoading
            }
          />
        </div>
      </div>
      <div className="mt-s rounded-xs bg-[#F8F8F8] p-l">
        <div className="flex justify-between">
          <div className="flex items-center gap-xs">
            <Text bold>Training Period</Text>
            <TimePickerInput
              value={trainingPeriod}
              isLive={false}
              onChange={setTrainingPeriod}
              maxDate={new Date()}
              position="left"
              showTimeOption={false}
            />
          </div>
          <div
            className="flex cursor-pointer items-center"
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
          >
            <Button variant="tertiary" title="Advanced" className="!px-xs" />
            <Icon
              icon={light('chevron-down')}
              className={classNames(
                'transition',
                isAdvancedOpen && 'rotate-180',
              )}
            />
          </div>
        </div>
        <AdvancedTrainingPeriod
          includedTimeRange={trainingPeriod}
          isOpen={isAdvancedOpen}
          setIsOpen={setIsAdvancedOpen}
          tagId={model.tag.tagName}
        />
      </div>
    </Card>
  )
}
